import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AnimalSymbol from "@/components/AnimalSymbol";
import { API_BASE } from "@/lib/config";
import { formatApiError, useAuth } from "@/contexts/AuthContext";

export default function ResetPasswordPage() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const tokenMissing = !token;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw !== confirm) { setErr("Passwords don't match."); return; }
    if (pw.length < 8) { setErr("Password must be at least 8 characters."); return; }
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/auth/reset-password`, { token, new_password: pw });
      await refresh();
      nav("/", { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2));
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--app-bg)] p-6">
      <div className="w-full max-w-sm bg-white border border-[var(--app-border)] rounded-lg p-8 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-md bg-[var(--app-bg)] border border-[var(--app-border)] flex items-center justify-center">
            <AnimalSymbol parts={{ ears: "correct", head: "correct", neck: "correct", front: "correct", back: "correct", rear: "correct", tail: "correct" }} size={32} />
          </div>
          <div className="leading-tight">
            <div className="font-display font-extrabold text-lg tracking-tight">Vitality Pedigree</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--app-text-soft)]">Set a new password</div>
          </div>
        </div>

        {tokenMissing ? (
          <div className="space-y-3" data-testid="reset-token-missing">
            <p className="text-sm text-red-600">No reset token in the URL. Please use the link from the email we sent you.</p>
            <Link to="/forgot-password" className="block">
              <Button variant="outline" className="w-full">Request a new link</Button>
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="reset-form">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[var(--app-text-soft)]" htmlFor="pw">New password</Label>
              <Input id="pw" type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="reset-password" />
              <div className="text-[10px] text-[var(--app-text-soft)] mt-1">Min 8 characters, with a letter and a number.</div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[var(--app-text-soft)]" htmlFor="confirm">Confirm</Label>
              <Input id="confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="reset-confirm" />
            </div>
            {err && <div className="text-sm text-red-600" data-testid="reset-error">{err}</div>}
            <Button type="submit" disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="reset-submit">
              {busy ? "Updating…" : "Set new password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
